import type { ComponentType } from 'react'

import { Box, Card, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { Text } from '@factorialco/deprecated-design-system'
import type {
  SurveysEnumsRespondentStrategyEnum,
  SurveysRespondentStrategyInput,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import type { WizardStepProps } from 'modules/engagement_surveys/components/SurveyWizard/types'

import { EmployeesStrategy } from './strategies/Employees'
import { LegalEntitiesStrategy } from './strategies/LegalEntities'
import { LocationsStrategy } from './strategies/Locations'
import { TeamsStrategy } from './strategies/Teams'

const strategyComponentMap: Record<
  SurveysEnumsRespondentStrategyEnum,
  | ComponentType<{ strategy: SurveysRespondentStrategyInput; onChange: (ids: number[]) => void }>
  | undefined
> = {
  employees: EmployeesStrategy,
  legal_entities: LegalEntitiesStrategy,
  locations: LocationsStrategy,
  teams: TeamsStrategy,
  all_employees: undefined,
  everyone: undefined,
  managers: undefined,
}

export function ParticipantsStep({ data, onDataChanged }: WizardStepProps) {
  const StrategyComponent = strategyComponentMap[data.respondentStrategy.strategy]

  return (
    <Box gap="s32" gapDivider>
      <Box gap="s24">
        <Text.Header2 weight="semibold">
          {i18n.t('surveys.configuration.participants.title')}
        </Text.Header2>

        <Box gap="s16">
          <SelectField
            label={i18n.t('surveys.configuration.participants.label')}
            value={data.respondentStrategy.strategy}
            onChange={(strategy) => {
              onDataChanged({
                ...data,
                // @ts-expect-error: Gamma select is not typed and we can't use assertions
                respondentStrategy: { strategy, arguments: [] },
              })
            }}
            options={[
              {
                // description: i18n.t('surveys.configuration.participants.options.everyone.description'),
                label: i18n.t('surveys.configuration.participants.options.everyone.label'),
                value: 'everyone',
              },
              {
                // description: i18n.t(
                //   'surveys.configuration.participants.options.all_employees.description'
                // ),
                label: i18n.t('surveys.configuration.participants.options.all_employees.label'),
                value: 'all_employees',
              },
              {
                // description: i18n.t('surveys.configuration.participants.options.employees.description'),
                label: i18n.t('surveys.configuration.participants.options.employees.label'),
                value: 'employees',
              },
              {
                // description: i18n.t('surveys.configuration.participants.options.managers.description'),
                label: i18n.t('surveys.configuration.participants.options.managers.label'),
                value: 'managers',
              },
              {
                // description: i18n.t('surveys.configuration.participants.options.teams.description'),
                label: i18n.t('surveys.configuration.participants.options.teams.label'),
                value: 'teams',
              },
              {
                // description: i18n.t('surveys.configuration.participants.options.locations.description'),
                label: i18n.t('surveys.configuration.participants.options.locations.label'),
                value: 'locations',
              },
              {
                // description: i18n.t(
                //   'surveys.configuration.participants.options.legal_entities.description'
                // ),
                label: i18n.t('surveys.configuration.participants.options.legal_entities.label'),
                value: 'legal_entities',
              },
            ]}
          />

          {StrategyComponent && (
            <StrategyComponent
              strategy={data.respondentStrategy}
              onChange={(ids) => {
                onDataChanged({
                  ...data,
                  respondentStrategy: { ...data.respondentStrategy, arguments: ids },
                })
              }}
            />
          )}
        </Box>
      </Box>

      <Box gap="s24">
        <Text.Header2 weight="semibold">
          {i18n.t('surveys.configuration.privacy_settings.title')}
        </Text.Header2>

        <Card>
          <Card.Title
            size="small"
            action={
              <TogglerCheckbox
                onChange={(value) => {
                  onDataChanged({ ...data, anonymous: value })
                }}
                value={data.anonymous}
              />
            }
          >
            {i18n.t('surveys.configuration.privacy_settings.anonymous_answers')}
          </Card.Title>
          <Card.Description>
            {i18n.t('surveys.configuration.privacy_settings.anonymous_answers_description')}
          </Card.Description>
        </Card>
      </Box>
    </Box>
  )
}
